/* Trame Atelier theme behaviour
   Smooth scroll, reveals and scroll scenes, product cards, the closer-look viewer,
   the bag drawer (Shopify cart), the shop and product pages, and the newsletter pop-up.
   Product data comes from snippets/trame-data.liquid as window.TRAME. */

(() => {
  const T = window.TRAME;
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const page = document.body.dataset.page || '';
  const initialQuery = new URLSearchParams(location.search); // read before any page rewrites the URL

  document.documentElement.classList.remove('no-js');

  /* ------------------------------------------------------------------ */
  /* Icons                                                               */
  /* ------------------------------------------------------------------ */

  const icon = {
    search: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 21 21"/></svg>',
    user: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.4-4 4.4-6 8-6s6.6 2 8 6"/></svg>',
    bag: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/></svg>',
    menu: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 8h18M3 16h18"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>',
    left: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    right: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>',
  };
  const url = (p, colour) => `${p.url}${colour ? `?colour=${colour}` : ''}`;

  /* ------------------------------------------------------------------ */
  /* Header (rendered by sections/header.liquid)                         */
  /* ------------------------------------------------------------------ */

  const header = $('#site-header');

  // Mega menu
  const megaBtn = $('.js-mega');
  const setMega = (open) => {
    header.classList.toggle('is-open', open);
    megaBtn.setAttribute('aria-expanded', open);
  };
  megaBtn?.addEventListener('click', () => setMega(!header.classList.contains('is-open')));
  header?.addEventListener('mouseleave', () => setMega(false));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setMega(false); closeCart(); setMenu(false); } });

  // Mobile menu
  const menuBtn = $('.js-menu');
  const setMenu = (open) => {
    document.body.classList.toggle('menu-open', open);
    if (!menuBtn) return;
    menuBtn.setAttribute('aria-expanded', open);
    menuBtn.innerHTML = open ? icon.close : icon.menu;
    $('#mobile-menu')?.setAttribute('aria-hidden', !open);
    lenis && (open ? lenis.stop() : lenis.start());
  };
  menuBtn?.addEventListener('click', () => setMenu(!document.body.classList.contains('menu-open')));

  // Header states: transparent over the hero, solid after, hides on scroll down
  const hasHero = !!$('[data-hero]');
  if (!hasHero) document.body.classList.add('header-solid');
  let lastY = 0;
  const onScroll = (y) => {
    if (!header) return;
    const solidAt = hasHero ? window.innerHeight * 0.82 : 10;
    header.classList.toggle('is-solid', y > solidAt);
    const hide = y > lastY && y > 320 && !header.classList.contains('is-open');
    header.classList.toggle('is-hidden', hide);
    document.body.classList.toggle('header-hidden', hide);
    lastY = y;
  };

  /* ------------------------------------------------------------------ */
  /* Smooth scroll (Lenis) + GSAP                                        */
  /* ------------------------------------------------------------------ */

  let lenis = null;
  const hasGsap = !!(window.gsap && window.ScrollTrigger);
  if (hasGsap) gsap.registerPlugin(ScrollTrigger);

  if (window.Lenis && !reduceMotion) {
    lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenis.on('scroll', ({ scroll }) => { onScroll(scroll); hasGsap && ScrollTrigger.update(); });
    if (hasGsap) {
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  } else {
    window.addEventListener('scroll', () => onScroll(window.scrollY), { passive: true });
  }

  // In-page links (e.g. "Request the trade catalogue") glide to their section
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a || a.getAttribute('href').length < 2) return;
    const target = document.querySelector(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    const offset = -((header ? header.offsetHeight : 0) + 8);
    lenis ? lenis.scrollTo(target, { offset }) : window.scrollTo({ top: target.getBoundingClientRect().top + scrollY + offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------ */
  /* Reveals                                                             */
  /* ------------------------------------------------------------------ */

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      // Reveal on entry, and also anything already scrolled past (e.g. after a jump link)
      if (!e.isIntersecting && e.boundingClientRect.top > 0) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -12% 0px' });
  const observeReveals = (root = document) => $$('[data-reveal], [data-clip]', root).forEach((el) => io.observe(el));

  // Stagger siblings that share a data-stagger parent
  $$('[data-stagger]').forEach((parent) => {
    [...parent.children].forEach((child, i) => {
      child.style.transitionDelay = `${i * 0.09}s`;
    });
  });

  /* ------------------------------------------------------------------ */
  /* Motion: text sweeps, pinned scenes and wipes (after agnestoth.com)  */
  /* ------------------------------------------------------------------ */

  // Wrap each word (and optionally each letter) in spans so it can be animated.
  // Words stay unbreakable so letters never wrap mid-word.
  const splitText = (el, mode) => {
    const parts = [];
    const label = (el.innerText || el.textContent).replace(/\s+/g, ' ').trim();
    const walk = (node) => {
      if (node.nodeType === 3) {
        const frag = document.createDocumentFragment();
        node.nodeValue.split(/(\s+)/).forEach((token) => {
          if (!token) return;
          if (!token.trim()) { frag.appendChild(document.createTextNode(token)); return; }
          const word = document.createElement('span');
          word.className = 'split-word';
          if (mode === 'char') {
            [...token].forEach((ch) => {
              const c = document.createElement('span');
              c.className = 'split-char';
              c.textContent = ch;
              word.appendChild(c);
              parts.push(c);
            });
          } else {
            word.textContent = token;
            parts.push(word);
          }
          frag.appendChild(word);
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === 1 && node.tagName !== 'BR') {
        [...node.childNodes].forEach(walk);
      }
    };
    walk(el);
    el.setAttribute('aria-label', label);
    parts.forEach((p) => p.setAttribute('aria-hidden', 'true'));
    return parts;
  };

  // Bring parts into focus in a sweep. 'x' sweeps by horizontal position across
  // all lines at once (the hero "typing"); 'order' follows reading order.
  const sweep = (parts, { axis = 'order', total = 1.4, part = 0.9, delay = 0, trigger = null, start = 'top 85%' } = {}) => {
    if (!parts.length) return;
    let stagger;
    if (axis === 'x') {
      const pos = parts.map((s) => s.getBoundingClientRect().left);
      const min = Math.min(...pos);
      const span = Math.max(Math.max(...pos) - min, 1);
      stagger = (i) => ((pos[i] - min) / span) * (total - part);
    } else {
      stagger = (i) => (i / Math.max(parts.length - 1, 1)) * (total - part);
    }
    const vars = { opacity: 1, filter: 'blur(0px)', duration: part, ease: 'power2.out', delay, stagger };
    if (trigger) vars.scrollTrigger = { trigger, start, once: true };
    gsap.to(parts, vars);
  };

  // Distance from the top of the document, unaffected by transforms or pin wrappers
  const docTop = (el) => { let y = 0; while (el) { y += el.offsetTop; el = el.offsetParent; } return y; };

  const motion = () => {
    if (!hasGsap || reduceMotion) return;

    /* Hero: letters come into focus left to right, like they're being typed */
    const hero = $('[data-hero]');
    if (hero) {
      const h1 = $('h1', hero);
      const sub = $('.hero__aside p', hero);
      const cta = $('.hero__aside .button, .hero__aside .link', hero);
      const media = $('.hero__media', hero) || $(':scope > img', hero);
      const parts = [...(h1 ? splitText(h1, 'char') : []), ...(sub ? splitText(sub, 'word') : [])];
      gsap.set(parts, { opacity: 0, filter: 'blur(8px)' });
      if (cta) gsap.set(cta, { autoAlpha: 0, y: 12 });
      if (media && media.classList.contains('hero__media')) {
        gsap.fromTo(media, { scale: 1.12 }, { scale: 1, duration: 2.6, ease: 'power2.out' });
      }
      let started = false;
      const go = () => {
        if (started) return;
        started = true;
        sweep(parts, { axis: 'x', total: 2.5, part: 1.2, delay: 0.2 });
        if (cta) gsap.to(cta, { autoAlpha: 1, y: 0, duration: 1, delay: 1.9, ease: 'power2.out' });
      };
      document.fonts?.ready ? document.fonts.ready.then(go) : go();
      setTimeout(go, 1200);
    }

    /* Inner page titles get the same typed-in letters as the hero */
    $$('.page-head h1').forEach((h) => {
      const chars = splitText(h, 'char');
      gsap.set(chars, { opacity: 0, filter: 'blur(8px)' });
      const go = () => sweep(chars, { axis: 'x', total: 1.8, part: 1, delay: 0.15 });
      document.fonts?.ready ? document.fonts.ready.then(go) : go();
    });

    /* Headings: words come into focus in reading order as they scroll in */
    $$('main h2.display-xl, main h2.display-l, main h2.display-m').forEach((h) => {
      if (h.closest('[data-hero], .statement')) return;
      h.removeAttribute('data-reveal');
      const words = splitText(h, 'word');
      gsap.set(words, { opacity: 0, filter: 'blur(8px)' });
      sweep(words, { total: 1.3, part: 0.9, trigger: h, start: 'top 88%' });
    });

    /* Statement: revealed top to bottom through a soft mask, out of a blur */
    const statement = $('.statement');
    if (statement) {
      const block = $('.wrap', statement);
      $$('[data-reveal]', block).forEach((el) => el.removeAttribute('data-reveal'));
      const state = { p: 0 };
      const mask = () => {
        const m = `linear-gradient(to bottom, #000 ${state.p - 40}%, transparent ${state.p}%)`;
        block.style.webkitMaskImage = m; block.style.maskImage = m;
      };
      mask();
      block.style.filter = 'blur(3px)';
      gsap.timeline({ scrollTrigger: { trigger: statement, start: 'top 75%', once: true } })
        .to(state, { p: 140, duration: 2.5, ease: 'power2.out', onUpdate: mask }, 0)
        .to(block, { filter: 'blur(0px)', duration: 2.5, ease: 'power2.out' }, 0)
        .add(() => { block.style.webkitMaskImage = block.style.maskImage = ''; block.style.filter = ''; });
    }

    /* Home collection: rises into view from the bottom, then cards follow */
    const collection = $('.collection');
    if (collection) {
      const cards = $$('.collection__grid > *', collection);
      gsap.set(cards, { y: 56, autoAlpha: 0 });
      ScrollTrigger.create({
        trigger: collection, start: 'top 80%', once: true,
        onEnter: () => gsap.to(cards, { y: 0, autoAlpha: 1, duration: 1.1, ease: 'power3.out', stagger: 0.12 }),
      });
    }

    /* Desktop scenes: pinned sections that the next one slides or wipes over */
    gsap.matchMedia().add('(min-width: 1024px)', () => {
      const vh = () => window.innerHeight;

      // Hero holds still while the next section slides up over it
      if (hero) {
        gsap.set(hero, { zIndex: 0 });
        // Shopify wraps each section in its own div, so look past the wrapper for the next section
        const next = hero.nextElementSibling || hero.closest('.shopify-section')?.nextElementSibling?.querySelector('section');
        if (next) gsap.set(next, { position: 'relative', zIndex: 1 });
        const content = $('.hero__content', hero) || $(':scope > div', hero);
        gsap.timeline({ scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, pin: true, pinSpacing: false } })
          .fromTo(hero, { yPercent: 0 }, { yPercent: -12, ease: 'none', duration: 1 }, 0)
          .fromTo(content, { opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(8px)', ease: 'none', duration: 0.35 }, 0);
      }

      // Statement holds, then the bags grow out of it as the brown melts into cream
      const pieces = $('.pieces');
      if (statement && pieces) {
        const dist = () => Math.max(docTop(pieces) - docTop(statement.parentElement.classList.contains('pin-spacer') ? statement.parentElement : statement), 1);
        gsap.set(statement, { zIndex: 1 });
        gsap.set(pieces, { position: 'relative', zIndex: 2 });
        gsap.timeline({ scrollTrigger: { trigger: statement, start: 'top top', end: () => '+=' + dist(), scrub: true, pin: true, pinSpacing: false, invalidateOnRefresh: true } })
          .fromTo(pieces, { y: () => -dist(), scale: 0.6, transformOrigin: () => `50% ${statement.offsetHeight / 2}px` }, { y: 0, scale: 1, ease: 'none', duration: 1 }, 0)
          // The statement lifts away crisply first, then the bags fade in, so the two never blur together
          .fromTo($('.wrap', statement), { opacity: 1, y: 0 }, { opacity: 0, y: -40, ease: 'none', duration: 0.3 }, 0)
          .fromTo(pieces, { autoAlpha: 0 }, { autoAlpha: 1, ease: 'none', duration: 0.2 }, 0.35)
          .fromTo(statement, { backgroundColor: '#593422' }, { backgroundColor: '#F4EBD5', ease: 'none', duration: 0.6 }, 0.2);
      }

      // Sections marked data-enter reveal themselves as they scroll in, then sit still so they can be read:
      // up = rises into place, wipe-x = opens left to right, wipe-y = opens top to bottom
      $$('[data-enter]').forEach((sec) => {
        const mode = sec.dataset.enter;
        const st = { trigger: sec, start: 'top 95%', end: 'top 25%', scrub: 0.6 };
        if (mode === 'wipe-x') {
          gsap.fromTo(sec, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', scrollTrigger: st });
        } else if (mode === 'wipe-y') {
          gsap.fromTo(sec, { clipPath: 'inset(0% 0% 100% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', scrollTrigger: st });
        } else {
          // The section box stays put (so its edges line up with its neighbours); the photo and text rise inside it
          const media = $('.split__media > img, .split__media > video', sec);
          const copy = $('.split__copy', sec);
          if (media) gsap.fromTo(media, { scale: 1.12 }, { scale: 1, ease: 'none', scrollTrigger: st });
          if (copy) gsap.fromTo(copy, { y: 60, opacity: 0 }, { y: 0, opacity: 1, ease: 'none', scrollTrigger: st });
        }
      });
    });
  };

  /* Parallax for editorial imagery */
  const parallax = () => {
    if (!hasGsap || reduceMotion) return;
    $$('[data-parallax]').forEach((el) => {
      const amount = parseFloat(el.dataset.parallax) || 10;
      gsap.fromTo(el, { yPercent: -amount }, {
        yPercent: amount, ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Product cards                                                       */
  /* ------------------------------------------------------------------ */

  const swatchesHTML = (p, active, cls = '') => `
    <div class="swatches ${cls}" role="group" aria-label="Colours">
      ${p.colours.map(c => `<button type="button" class="swatch" style="--c:${T.colours[c].hex}" data-colour="${c}" aria-pressed="${c === active}" aria-label="${T.colours[c].name}"></button>`).join('')}
    </div>`;

  const cardHTML = (p, colour, { swatches = true, tag = true } = {}) => {
    const name = colour && T.colours[colour] ? T.colours[colour].name : '';
    return `
    <article class="card" data-card="${p.handle}" data-colour="${colour || ''}">
      <a class="card__media" href="${url(p, colour)}" aria-label="${p.name}${name ? `, ${name}` : ''}">
        ${tag && p.tag ? `<span class="card__tag">${p.tag}</span>` : ''}
        <img src="${T.img(p.handle, colour, 1)}" alt="${p.name}${name ? ` in ${name}` : ''}" loading="lazy">
        <img src="${T.img(p.handle, colour, 2)}" alt="" loading="lazy">
      </a>
      <div class="card__meta">
        <a class="card__name" href="${url(p, colour)}">${p.name}</a>
        <span class="card__price">${T.cardPrice(p)}</span>
        <span class="card__colour" data-colour-name>${name}</span>
        ${swatches && p.colours.length > 1 ? `<div class="card__swatches">${swatchesHTML(p, colour)}</div>` : ''}
      </div>
    </article>`;
  };

  // Swatch clicks on any card swap its imagery and link
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.card .swatch');
    if (!sw) return;
    const card = sw.closest('.card');
    const p = T.byHandle[card.dataset.card];
    const c = sw.dataset.colour;
    card.dataset.colour = c;
    $$('.swatch', card).forEach(b => b.setAttribute('aria-pressed', b === sw));
    const [a, b] = $$('.card__media img', card);
    a.src = T.img(p.handle, c, 1); a.alt = `${p.name} in ${T.colours[c].name}`;
    b.src = T.img(p.handle, c, 2);
    $$('a', card).forEach(l => { l.href = url(p, c); });
    $('[data-colour-name]', card).textContent = T.colours[c].name;
  });

  /* ------------------------------------------------------------------ */
  /* Home: collection grid                                               */
  /* ------------------------------------------------------------------ */

  const homeGrid = $('[data-home-grid]');
  if (homeGrid) {
    let picks = [];
    try { picks = JSON.parse(homeGrid.dataset.cards || '[]'); } catch { picks = []; }
    let list = picks.filter(c => T.byHandle[c.handle]).map(c => [T.byHandle[c.handle], T.key(c.colour || '')]);
    if (!list.length) list = T.products.slice(0, 6).map(p => [p, p.hero]);
    list = list.map(([p, c]) => [p, p.colours.includes(c) ? c : p.hero]);
    homeGrid.insertAdjacentHTML('afterbegin', list.map(([p, c]) => cardHTML(p, c)).join(''));
  }

  /* ------------------------------------------------------------------ */
  /* Closer look: colour + angle viewer                                  */
  /* ------------------------------------------------------------------ */

  const closer = $('[data-closer]');
  const closerProduct = closer && T.byHandle[closer.dataset.closer];
  if (closer && !closerProduct) $('.closer__stage', closer).hidden = true;
  if (closer && closerProduct) {
    const p = closerProduct;
    const frame = $('.closer__frame', closer);
    const picker = $('.closer__picker', closer);
    const label = $('.closer__picker-label', closer);
    const dots = $('.closer__dots', closer);
    const angles = 3;
    let colour = p.colours.includes(T.key(closer.dataset.colour || '')) ? T.key(closer.dataset.colour) : p.hero;
    let angle = 0;

    picker.insertAdjacentHTML('afterbegin', swatchesHTML(p, colour, 'swatches--lg'));
    dots.innerHTML = Array.from({ length: angles }, () => '<span></span>').join('');

    const render = () => {
      frame.innerHTML = Array.from({ length: angles }, (_, i) =>
        `<img src="${T.img(p.handle, colour, i + 1)}" alt="${i === angle ? `${p.name} in ${T.colours[colour].name}` : ''}" draggable="false"${i === angle ? ' class="is-active"' : ''}>`).join('');
      label.textContent = T.colours[colour].name;
      $$('.swatch', picker).forEach(b => b.setAttribute('aria-pressed', b.dataset.colour === colour));
      const link = $('[data-closer-link]', closer); if (link) link.href = url(p, colour);
      setAngle(angle);
    };
    const setAngle = (i) => {
      angle = (i + angles) % angles;
      $$('img', frame).forEach((img, n) => img.classList.toggle('is-active', n === angle));
      $$('span', dots).forEach((d, n) => d.classList.toggle('is-active', n === angle));
    };

    picker.addEventListener('click', (e) => {
      const sw = e.target.closest('.swatch');
      if (!sw) return;
      colour = sw.dataset.colour;
      render();
    });
    $('.js-prev', closer).addEventListener('click', () => setAngle(angle - 1));
    $('.js-next', closer).addEventListener('click', () => setAngle(angle + 1));

    // Drag to turn the bag
    let startX = null;
    frame.addEventListener('pointerdown', (e) => { startX = e.clientX; frame.setPointerCapture(e.pointerId); });
    frame.addEventListener('pointermove', (e) => {
      if (startX == null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 60) { setAngle(angle + (dx < 0 ? 1 : -1)); startX = e.clientX; }
    });
    ['pointerup', 'pointercancel'].forEach(t => frame.addEventListener(t, () => { startX = null; }));
    frame.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') setAngle(angle - 1);
      if (e.key === 'ArrowRight') setAngle(angle + 1);
    });

    render();
  }

  /* ------------------------------------------------------------------ */
  /* Cart (prototype only — Shopify's cart API replaces this)            */
  /* ------------------------------------------------------------------ */

  let cart = { items: [], item_count: 0, total_price: 0 };
  const money = (cents) => T.formatPrice(cents / 100);

  // One-of-a-kind necklaces never get a quantity stepper
  const isNecklace = (l) => l.product_type === 'Necklace' || T.necklaces.some(n => n.variant === (l.variant_id || l.id));

  const renderCart = () => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.classList.toggle('has-items', cart.item_count > 0); });
    const lines = $('[data-cart-lines]');
    const foot = $('[data-cart-foot]');
    if (!lines) return;
    if (!cart.items.length) {
      lines.innerHTML = `<div class="drawer__empty"><p class="display-s">Your bag is empty.</p><p class="small muted">${T.strings.emptyBag || ''}</p><a class="button" href="${T.routes.shop}">Shop the collection</a></div>`;
      foot.innerHTML = '';
      return;
    }
    lines.innerHTML = cart.items.map((l) => `<div class="line">
        ${l.image ? `<img src="${l.image}${l.image.includes('?') ? '&' : '?'}width=200" alt="">` : '<span></span>'}
        <div>
          <a class="line__name" href="${l.url}">${l.product_title}</a>
          ${l.variant_title ? `<div class="line__meta">${l.variant_title}</div>` : ''}
          ${Object.entries(l.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => `<div class="line__meta">${k}: ${v}</div>`).join('')}
          ${(l.properties && l.properties._pair) || isNecklace(l) ? '' : `<div class="qty"><button type="button" data-qty="${l.key}" data-n="${l.quantity - 1}" aria-label="Decrease quantity">−</button><span>${l.quantity}</span><button type="button" data-qty="${l.key}" data-n="${l.quantity + 1}" aria-label="Increase quantity">+</button></div>`}
        </div>
        <div style="display:grid;justify-items:end;align-content:space-between">
          <span>${money(l.final_line_price)}</span>
          <button type="button" class="line__remove" data-qty="${l.key}" data-n="0">Remove</button>
        </div>
      </div>`).join('');
    foot.innerHTML = `
      <div class="drawer__total"><span>Subtotal</span><span>${money(cart.total_price)}</span></div>
      <p class="small muted">${T.strings.cartNote || ''}</p>
      <form action="${T.routes.cart}" method="post"><button class="button button--solid button--block" type="submit" name="checkout">Check out</button></form>`;
  };

  const fetchCart = () => fetch(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } })
    .then(r => r.json()).then((c) => { cart = c; renderCart(); }).catch(() => {});

  const openCart = () => { document.body.classList.add('drawer-open'); $('#cart-drawer')?.setAttribute('aria-hidden', 'false'); lenis?.stop(); };
  const closeCart = () => { document.body.classList.remove('drawer-open'); $('#cart-drawer')?.setAttribute('aria-hidden', 'true'); lenis?.start(); };

  const addToCart = (id, quantity) => addItems([{ id, quantity }]);
  const addItems = (items) => fetch(`${T.routes.cartAdd}.js`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  }).then(async (r) => {
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.description || 'That bag could not be added. Please try again.'); }
    await fetchCart(); openCart();
  });

  const changeLine = (key, quantity) => fetch(`${T.routes.cartChange}.js`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  }).then(r => (r.ok ? r.json() : null)).then((c) => { if (c) { cart = c; renderCart(); } }).catch(() => {});

  // Cart page: removing a bag or its necklace removes both, then reloads the page
  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-pair-remove]');
    if (!rm) return;
    e.preventDefault();
    for (const el of $$(`[data-line-pair="${CSS.escape(rm.dataset.pairRemove)}"]`)) await changeLine(el.dataset.lineKey, 0);
    location.reload();
  });

  document.addEventListener('click', async (e) => {
    const opener = e.target.closest('.js-cart-open');
    if (opener && page !== 'cart') { e.preventDefault(); openCart(); }
    if (e.target.closest('.js-cart-close')) closeCart();
    const q = e.target.closest('[data-qty]');
    if (!q) return;
    // A bag and the necklace chosen for it share a hidden _pair id, so removing one removes both
    const line = cart.items.find(l => l.key === q.dataset.qty);
    const pair = line && line.properties && line.properties._pair;
    if (pair && +q.dataset.n === 0) {
      for (const l of cart.items.filter(x => x.properties && x.properties._pair === pair)) await changeLine(l.key, 0);
    } else {
      changeLine(q.dataset.qty, +q.dataset.n);
    }
  });

  // Necklaces bought on their own (shop section and a necklace's own page)
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-necklace-add]');
    if (!b) return;
    const label = b.textContent;
    b.disabled = true;
    addToCart(+b.dataset.necklaceAdd, 1)
      .then(() => { b.textContent = 'In your bag'; })
      .catch(() => { b.textContent = 'Already in your bag'; })
      .finally(() => setTimeout(() => { b.disabled = false; b.textContent = label; }, 2500));
  });

  /* ------------------------------------------------------------------ */
  /* Shop page                                                           */
  /* ------------------------------------------------------------------ */

  // Jade necklaces under the bags: one of a kind, so only available ones are listed
  const necklaceShop = $('[data-necklace-shop]');
  if (necklaceShop && T.necklaces.length) {
    necklaceShop.hidden = false;
    $('[data-necklace-grid]', necklaceShop).innerHTML = T.necklaces.map(n => `
      <article class="card card--necklace">
        <a class="card__media" href="${n.url}" aria-label="${n.name}">
          <span class="card__tag">One of a kind</span>
          ${n.img ? `<img src="${n.img}" alt="${n.name}${n.note ? `, ${n.note}` : ''}" loading="lazy">` : ''}
          ${n.img2 ? `<img src="${n.img2}" alt="" loading="lazy">` : ''}
        </a>
        <div class="card__meta">
          <a class="card__name" href="${n.url}">${n.name}</a>
          ${n.note ? `<span class="card__colour">${n.note}</span>` : ''}
          <span class="card__price">${T.formatPrice(n.price / 100)}</span>
        </div>
        <button type="button" class="button card__add" data-necklace-add="${n.variant}">Add to bag</button>
      </article>`).join('');
    if (location.hash === '#necklaces') {
      // Wait until the bags above are in place, then let the smooth scroller re-measure the page
      const go = () => {
        const bar = $('.filters');
        const y = necklaceShop.getBoundingClientRect().top + scrollY - (header ? header.offsetHeight : 0) - (bar ? bar.offsetHeight : 0) - 8;
        if (lenis) { lenis.resize(); lenis.scrollTo(y, { immediate: true, force: true }); } else scrollTo(0, y);
      };
      setTimeout(go, 400);
      addEventListener('load', () => setTimeout(go, 100), { once: true });
    }
  }

  const shopGrid = $('[data-shop-grid]');
  if (shopGrid) {
    const params = new URLSearchParams(location.search);
    let style = params.get('style') || 'all';
    let colour = params.get('colour') || null;
    const breakHTML = shopGrid.innerHTML;
    let handles = [];
    try { handles = JSON.parse(shopGrid.dataset.handles || '[]'); } catch { handles = []; }
    const shopProducts = handles.length ? handles.map(h => T.byHandle[h]).filter(Boolean) : T.products;
    const shopColours = T.order.filter(c => shopProducts.some(p => p.colours.includes(c)));

    const styleChips = $('[data-style-chips]');
    styleChips.innerHTML = [['all', 'All bags'], ...shopProducts.map(p => [p.handle, p.name])]
      .map(([v, l]) => `<button type="button" class="chip" data-style="${v}" aria-pressed="${v === style}">${l}</button>`).join('');

    const colourWrap = $('[data-colour-filter]');
    colourWrap.innerHTML = `<span>Colour</span><div class="swatches" role="group" aria-label="Filter by colour">${T.order.map(c =>
      `<button type="button" class="swatch" style="--c:${T.colours[c].hex}" data-filter-colour="${c}" aria-pressed="${c === colour}" aria-label="${T.colours[c].name}"></button>`).join('')}</div>
      <button type="button" class="link" data-clear-colour ${colour ? '' : 'hidden'}>Clear</button>`;

    const render = () => {
      const list = shopProducts.filter(p => (style === 'all' || p.handle === style) && (!colour || p.colours.includes(colour)));
      const cards = [];
      list.forEach((p) => {
        if (style !== 'all') {
          // One card per colour when a single style is chosen
          (colour ? [colour] : p.colours).forEach(c => cards.push(cardHTML(p, c, { swatches: false })));
        } else {
          cards.push(cardHTML(p, colour && p.colours.includes(colour) ? colour : p.hero));
        }
      });
      const html = cards.length ? [...cards] : ['<p class="shop-empty display-s">Nothing matches those filters yet.</p>'];
      if (style === 'all' && html.length > 3) html.splice(3, 0, breakHTML);
      shopGrid.innerHTML = html.join('');
      $('[data-count]').textContent = `${cards.length} ${cards.length === 1 ? 'piece' : 'pieces'}`;
      $$('[data-style]').forEach(b => b.setAttribute('aria-pressed', b.dataset.style === style));
      $$('[data-filter-colour]').forEach(b => b.setAttribute('aria-pressed', b.dataset.filterColour === colour));
      $('[data-clear-colour]').hidden = !colour;
      const q = new URLSearchParams();
      if (style !== 'all') q.set('style', style);
      if (colour) q.set('colour', colour);
      history.replaceState(null, '', (q.toString() ? `?${q}` : location.pathname) + location.hash);
      hasGsap && ScrollTrigger.refresh();
    };

    document.addEventListener('click', (e) => {
      const s = e.target.closest('[data-style]');
      if (s) { style = s.dataset.style; render(); }
      const c = e.target.closest('[data-filter-colour]');
      if (c) { colour = colour === c.dataset.filterColour ? null : c.dataset.filterColour; render(); }
      if (e.target.closest('[data-clear-colour]')) { colour = null; render(); }
    });
    render();
  }

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */

  const pdp = $('[data-pdp]');
  const pdpProduct = pdp && T.byHandle[pdp.dataset.pdp];
  if (pdp && pdpProduct) {
    const p = pdpProduct;
    const params = new URLSearchParams(location.search);
    const fromVariant = p.variants.find(v => String(v.id) === params.get('variant'));
    const want = params.get('colour') || (fromVariant && p.colourOption ? T.key(fromVariant.options[p.colourOption - 1]) : null);
    let colour = p.colours.includes(want) ? want : p.hero;
    let choice = p.choice ? (fromVariant ? fromVariant.options[p.choice.position - 1] : p.choice.names[0]) : null;
    let qty = 1;
    const errorBox = $('[data-add-error]');

    // Necklaces (La Juliette): one of a kind, so one per bag and quantity stays at 1
    const necklaces = p.pairsNecklace ? T.necklaces : [];
    let necklace = necklaces.find(n => n.handle === params.get('necklace')) || null;

    const showPrice = () => {
      const v = T.variantFor(p, colour, choice);
      const total = v.price + (necklace ? necklace.price : 0);
      $('[data-price]').innerHTML = T.formatPrice(total / 100) + (necklace
        ? ` <span class="pdp__price-split">${p.name} ${T.formatPrice(v.price / 100)} + necklace ${T.formatPrice(necklace.price / 100)}</span>` : '');
      const btn = $('[data-add]');
      btn.disabled = !v.available;
      btn.textContent = v.available ? 'Add to bag' : 'Sold out';
      if (necklace) { qty = 1; $('[data-qty-value]').textContent = 1; }
      $('.pdp__buy .qty')?.classList.toggle('is-locked', !!necklace);
    };

    const necklaceWrap = $('[data-necklace-option]');
    const necklaceName = () => (necklace ? necklace.name : pdp.dataset.necklaceNone);
    if (necklaces.length) {
      necklaceWrap.hidden = false;
      const none = `<button type="button" class="necklace" role="radio" data-necklace="" aria-checked="${!necklace}">
          <span class="necklace__img necklace__img--none"><svg aria-hidden="true"><use href="#ta-icon"/></svg></span>
          <span class="necklace__name">${pdp.dataset.necklaceNone}</span></button>`;
      $('[data-necklaces]').innerHTML = none + necklaces.map(n => `<button type="button" class="necklace" role="radio" data-necklace="${n.handle}" aria-checked="${n === necklace}" aria-label="${n.name}${n.note ? `, ${n.note}` : ''}, ${T.formatPrice(n.price / 100)}">
          <span class="necklace__img">${n.img ? `<img src="${n.img}" alt="" loading="lazy">` : ''}</span>
          <span class="necklace__name">${n.name}</span>
          ${n.note ? `<span class="necklace__stones">${n.note}</span>` : ''}
          <span class="necklace__price">+ ${T.formatPrice(n.price / 100)}</span></button>`).join('');
      $('[data-necklace-name]').textContent = necklaceName();
    }

    if (p.colours.length) {
      $('[data-colour-option]').hidden = false;
      $('[data-pdp-swatches]').innerHTML = p.colours.map(c =>
        `<button type="button" class="swatch" role="radio" style="--c:${T.colours[c].hex}" data-pick="${c}" aria-checked="${c === colour}" aria-label="${T.colours[c].name}"></button>`).join('');
    }

    const choiceWrap = $('[data-choice]');
    if (p.choice) {
      choiceWrap.hidden = false;
      $('.pdp__option-label', choiceWrap).textContent = p.choice.label;
      $('.segmented', choiceWrap).setAttribute('aria-label', p.choice.label);
      $('.segmented', choiceWrap).innerHTML = p.choice.names.map(n =>
        `<button type="button" role="radio" data-choice-pick="${n}" aria-checked="${n === choice}">${n}<br><span class="muted">${T.formatPrice(T.priceOf(p, n, colour))}</span></button>`).join('');
    }

    const gallery = $('[data-gallery]');
    const colourName = () => (T.colours[colour] ? T.colours[colour].name : '');
    const renderGallery = () => {
      const shots = [1, 2, 3].map(n => T.img(p.handle, colour, n)).filter(Boolean);
      if (!shots.length) return;   // keep the server-rendered photos
      const pics = shots.map((s, i) => `<figure><img src="${s}" alt="${p.name} in ${colourName()}, view ${i + 1}"></figure>`);
      const clip = T.film(p.handle, colour);
      // The colour's own film sits second; colours without footage show photos only
      const extra = clip ? `<figure class="pdp__film"><video src="${clip}" muted loop playsinline autoplay preload="metadata" aria-label="${p.name} in ${colourName()}, turned and opened"></video></figure>` : null;
      const list = extra ? [pics[0], extra, ...pics.slice(1)] : pics;
      const extraPics = (p.images[colour] || []).slice(3, extra ? 3 : 4)
        .map((s, i) => `<figure><img src="${s}" alt="${p.name} in ${colourName()}, view ${i + 4}" loading="lazy"></figure>`);
      gallery.innerHTML = list.concat(extraPics).join('');
      const v = $('video', gallery);
      if (v && !reduceMotion) v.play().catch(() => {});
    };
    const setColour = (c) => {
      colour = c;
      $('[data-pdp-colour]').textContent = colourName();
      $$('[data-pick]').forEach(b => b.setAttribute('aria-checked', b.dataset.pick === c));
      gallery.classList.add('is-swapping');
      setTimeout(() => { renderGallery(); gallery.classList.remove('is-swapping'); }, reduceMotion ? 0 : 260);
      history.replaceState(null, '', `${p.url}?colour=${c}`);
      showPrice();
    };

    pdp.addEventListener('click', (e) => {
      const pick = e.target.closest('[data-pick]');
      if (pick) setColour(pick.dataset.pick);
      const q = e.target.closest('[data-pdp-qty]');
      if (q && !necklace) { qty = Math.max(1, qty + +q.dataset.pdpQty); $('[data-qty-value]').textContent = qty; }
      const nk = e.target.closest('[data-necklace]');
      if (nk) {
        necklace = necklaces.find(n => n.handle === nk.dataset.necklace) || null;
        $$('[data-necklace]').forEach(b => b.setAttribute('aria-checked', b === nk));
        $('[data-necklace-name]').textContent = necklaceName();
        errorBox.textContent = '';
        showPrice();
      }
      const cp = e.target.closest('[data-choice-pick]');
      if (cp) { choice = cp.dataset.choicePick; $$('[data-choice-pick]').forEach(b => b.setAttribute('aria-checked', b === cp)); showPrice(); }
      const add = e.target.closest('[data-add]');
      if (add) {
        add.disabled = true;
        errorBox.textContent = '';
        const bagId = T.variantFor(p, colour, choice).id;
        let items = [{ id: bagId, quantity: qty }];
        if (necklace) {
          const pair = `${bagId}-${necklace.variant}-${Date.now()}`;
          items = [
            { id: bagId, quantity: 1, properties: { Necklace: necklace.name, _pair: pair } },
            { id: necklace.variant, quantity: 1, properties: { 'Paired with': `${p.name}, ${colourName()}`, _pair: pair } },
          ];
        }
        addItems(items)
          .catch((err) => {
            errorBox.textContent = necklace ? `Sorry, ${necklace.name} is already in your bag or no longer available. Please choose another necklace.` : err.message;
            errorBox.classList.add('is-visible');
          })
          .finally(() => { showPrice(); });
      }
    });

    if (p.colours.length) $('[data-pdp-colour]').textContent = colourName();
    showPrice();
    renderGallery();

    // "Inside every bag": this bag's own media (alt text containing "inside"), if it has one
    const storyMedia = $('[data-story-media]');
    if (storyMedia && p.inside) {
      storyMedia.innerHTML = p.inside.video
        ? `<video src="${p.inside.video}" poster="${p.inside.poster}" muted loop playsinline preload="none" data-autoplay aria-label="Inside ${p.name}, showing the printed satin lining"></video>`
        : `<img src="${p.inside.image}" alt="Inside ${p.name}, showing the printed satin lining" loading="lazy">`;
    }

    // Related pieces
    const related = $('[data-related]');
    if (related) related.innerHTML = T.products.filter(x => x.handle !== p.handle).slice(0, 4)
      .map(x => cardHTML(x, x.colours.includes(colour) ? colour : x.hero)).join('');
  }

  /* ------------------------------------------------------------------ */
  /* Newsletter sign-up                                                  */
  /* Same fields Shopify's customer form expects (form_type=customer,    */
  /* contact[email], contact[tags]=newsletter). In the theme this markup */
  /* comes from snippets/newsletter-form.liquid; here nothing is sent.   */
  /* ------------------------------------------------------------------ */

  const NL_KEY = 'trame-newsletter';
  const nlStore = {
    get() { try { return JSON.parse(localStorage.getItem(NL_KEY) || '{}'); } catch { return {}; } },
    set(v) { try { localStorage.setItem(NL_KEY, JSON.stringify({ ...nlStore.get(), ...v })); } catch { /* storage unavailable */ } },
  };

  // A successful sign-up comes back with a thank-you in the form: remember it so the pop-up stays away
  if ($('[data-nl-success]')) nlStore.set({ subscribed: true });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-nl-form]');
    if (!form) return;
    const input = $('input[type="email"]', form);
    const status = $('.nl-form__status', form);
    form.classList.remove('is-error');
    if (input && (!input.value.trim() || !input.checkValidity())) {
      e.preventDefault();
      form.classList.add('is-error');
      if (status) status.textContent = 'Enter an email address like name@example.com.';
      input.focus();
    }
  });

  /* Pop-up: once per visitor, after they've had time to look around */
  const popup = $('.nl-popup');
  let lastFocus = null;

  const openPopup = () => {
    if (!popup) return;
    const state = nlStore.get();
    if (state.subscribed || (state.dismissedAt && Date.now() - state.dismissedAt < 30 * 864e5)) return;
    if (document.body.classList.contains('drawer-open') || document.body.classList.contains('menu-open')) return;
    lastFocus = document.activeElement;
    popup.hidden = false;
    requestAnimationFrame(() => popup.classList.add('is-open'));
    lenis?.stop();
    setTimeout(() => $('input[type="email"]', popup)?.focus({ preventScroll: true }), 300);
  };
  const closePopup = () => {
    if (!popup || popup.hidden) return;
    popup.classList.remove('is-open');
    if (!nlStore.get().subscribed) nlStore.set({ dismissedAt: Date.now() });
    lenis?.start();
    setTimeout(() => { popup.hidden = true; lastFocus?.focus?.({ preventScroll: true }); }, reduceMotion ? 0 : 450);
  };
  popup?.addEventListener('click', (e) => { if (e.target.closest('[data-nl-close]')) closePopup(); });
  document.addEventListener('keydown', (e) => {
    if (!popup || popup.hidden) return;
    if (e.key === 'Escape') closePopup();
    if (e.key === 'Tab') { // keep focus inside the dialog
      const f = $$('button, input, a[href]', popup).filter(el => el.offsetParent);
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  });

  // Show after 20 seconds, or once the visitor has scrolled through half the page, whichever comes first.
  // Never on the wholesale page, where visitors are there to enquire. ?newsletter=1 forces it for review.
  if (popup && !page.startsWith('page-wholesale') && page !== 'cart') {
    const force = initialQuery.has('newsletter');
    if (force) { try { localStorage.removeItem(NL_KEY); } catch { /* ignore */ } setTimeout(openPopup, 600); }
    else {
      let shown = false;
      const trigger = () => { if (!shown) { shown = true; openPopup(); } };
      setTimeout(trigger, (+popup.dataset.popupDelay || 20) * 1000);
      const onDeep = () => {
        const y = lenis ? lenis.scroll : window.scrollY;
        if (y > (document.documentElement.scrollHeight - innerHeight) * 0.5) { trigger(); window.removeEventListener('scroll', onDeep); }
      };
      window.addEventListener('scroll', onDeep, { passive: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Instagram marquee: duplicate items so the loop is seamless          */
  /* ------------------------------------------------------------------ */

  $$('.marquee__track').forEach((track) => { track.innerHTML += track.innerHTML; });

  /* ------------------------------------------------------------------ */
  /* Videos: play only while visible                                     */
  /* ------------------------------------------------------------------ */

  const vio = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && !reduceMotion) v.play().catch(() => {});
      else v.pause();
    });
  }, { threshold: 0.15 });
  // Videos with a phone-sized version load only the file that suits the screen
  $$('video[data-src]').forEach((v) => {
    const small = v.dataset.srcSmall && window.matchMedia('(max-width: 900px)').matches;
    v.src = small ? v.dataset.srcSmall : v.dataset.src;
  });
  $$('video[data-autoplay]').forEach((v) => { v.muted = true; vio.observe(v); });

  /* ------------------------------------------------------------------ */
  /* Go                                                                  */
  /* ------------------------------------------------------------------ */

  fetchCart();
  motion();
  observeReveals();
  parallax();
  onScroll(window.scrollY);
  window.addEventListener('load', () => hasGsap && ScrollTrigger.refresh());
})();
